<template>
    <section class="ds-ex-card ds-ex-form" aria-label="Новый запрос">
        <div class="ds-ex-form-row">
            <div class="ds-ex-form-title">
                <div class="ds-ex-form-name">Новый запрос</div>
                <div class="ds-ex-muted">Сотрудник из другого магазина в ваш</div>
            </div>
            <div class="ds-ex-form-field">
                <span class="ds-ex-label">Дата</span>
                <div class="ds-ex-date" :class="{ 'is-disabled': saving }"><AppDateInput :model-value="dateValue" :min-date="today" placeholder="Дата" @update:model-value="onDate" /></div>
            </div>
            <div class="ds-ex-form-field">
                <span class="ds-ex-label">Начало</span>
                <div class="ds-ex-time"><TimePicker v-model="draft.start" :disabled="saving" /></div>
            </div>
            <div class="ds-ex-form-field">
                <span class="ds-ex-label">Окончание</span>
                <div class="ds-ex-time"><TimePicker v-model="draft.end" :disabled="saving" /></div>
            </div>
            <div class="ds-ex-form-field ds-ex-form-store">
                <span class="ds-ex-label">Из магазина</span>
                <ExchangeSelect
                    v-model="fromDepartmentId"
                    label="Из магазина"
                    placeholder="Выберите магазин"
                    searchable
                    remote-search
                    search-placeholder="Найти магазин"
                    :options="departmentOptions"
                    :disabled="saving"
                    @search="directory.searchDepartments"
                />
            </div>
            <div class="ds-ex-form-field ds-ex-form-employee">
                <span class="ds-ex-label">Сотрудник</span>
                <ExchangeSelect
                    v-model="draft.employeeId"
                    label="Сотрудник"
                    :placeholder="employeePlaceholder"
                    searchable
                    search-placeholder="Найти сотрудника"
                    :options="employeeOptions"
                    :heading="employeesHeading"
                    footnote="Показаны сотрудники без смен в это время"
                    :empty-text="employeesError ? 'Не удалось загрузить сотрудников' : 'Свободных сотрудников нет'"
                    :loading="employeesLoading"
                    :disabled="saving || !intervalReady"
                />
            </div>
            <div class="ds-ex-form-field ds-ex-form-position">
                <span class="ds-ex-label">Должность у вас</span>
                <ExchangeSelect v-model="draft.positionId" label="Должность у вас" placeholder="Выберите должность" searchable search-placeholder="Найти должность" :options="positionOptions" :disabled="saving" />
            </div>
        </div>
        <div class="ds-ex-form-row is-secondary">
            <button v-if="!draft.commentOpen" type="button" class="ds-ex-link-btn" :disabled="saving" @click="draft.commentOpen = true">+ Комментарий</button>
            <label v-else class="ds-ex-form-field ds-ex-grow">
                <span class="ds-ex-label">Комментарий</span>
                <input v-model="draft.comment" type="text" class="ds-ex-field ds-ex-input" maxlength="100" :disabled="saving">
            </label>
            <div class="ds-ex-spacer"></div>
            <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="!canSubmit" @click="submit">{{ saving ? 'Отправляем…' : 'Отправить запрос' }}</button>
        </div>
        <div v-if="!department" class="ds-ex-form-alert" role="alert">Не удалось определить магазин пользователя — запрос недоступен.</div>
        <div v-if="formError" class="ds-ex-form-alert" role="alert">{{ formError }}</div>
    </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import debounce from 'lodash.debounce'
import AppDateInput from '@/ui/AppDateInput.vue'
import TimePicker from '@/components/TimePicker.vue'
import { useCalendarToday } from '@/ui/calendarTime'
import ExchangeSelect from './ExchangeSelect.vue'
import { exchangeApi } from './exchangeApi'
import { errorText } from './exchangeActions'
import { exchangeToast } from './exchangeToasts'
import { useExchangeDirectory } from './exchangeDirectory'
import { useExchangePage } from './exchangePage'
import { buildTimestamp, fullName, instantOf, rowDateLabel, wallNow } from './exchangeFormat'
import type { AvailableEmployee } from './exchangeTypes'

const emit = defineEmits<{ created: [] }>()

const page = useExchangePage()
const draft = page.requestDraft
const directory = useExchangeDirectory()
const today = useCalendarToday()

const saving = ref(false)
const formError = ref('')
const employees = ref<AvailableEmployee[]>([])
const employeesLoading = ref(false)
const employeesError = ref(false)
let employeesSeq = 0

const department = computed(() => directory.profileDepartment.value)
const dateValue = computed(() => draft.date ? dayjs(draft.date) : null)
const positionOptions = computed(() => directory.positions.value.map(item => ({ id: item.id, name: item.name })))
const departmentOptions = computed(() => {
    const list = directory.departments.value.map(item => ({ id: item.id, name: item.name }))
    const current = draft.fromDepartment
    if (current && !list.some(item => item.id === current.id)) list.unshift({ id: current.id, name: current.name })
    return list
})
const fromDepartmentId = computed({
    get: () => draft.fromDepartment?.id ?? '',
    set: id => { draft.fromDepartment = departmentOptions.value.find(item => item.id === id) ?? null },
})

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
const intervalReady = computed(() => Boolean(draft.date && draft.start && draft.end && TIME.test(draft.start) && TIME.test(draft.end) && draft.start < draft.end && draft.fromDepartment))
const employeeOptions = computed(() => employees.value.map(item => ({ id: item.id, name: fullName(item), subtitle: item.position?.name ?? '', person: item })))
const employeesHeading = computed(() => intervalReady.value ? `СВОБОДНЫ ${dayjs(draft.date!).format('DD.MM')} · ${draft.start}–${draft.end}` : '')
const employeePlaceholder = computed(() => intervalReady.value ? 'Выберите сотрудника' : 'Сначала магазин и время')
const canSubmit = computed(() => Boolean(!saving.value && department.value && intervalReady.value && draft.employeeId && draft.positionId))

function onDate(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    draft.date = dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : null
}

// Устаревший ответ отбрасывается и не восстанавливает прежний выбор.
async function loadEmployees() {
    const my = ++employeesSeq
    employees.value = []
    employeesError.value = false
    if (!intervalReady.value) {
        employeesLoading.value = false
        return
    }
    employeesLoading.value = true
    try {
        const list = await exchangeApi.availableEmployees(
            draft.fromDepartment!.id,
            buildTimestamp(draft.date!, draft.start!),
            buildTimestamp(draft.date!, draft.end!),
        )
        if (my === employeesSeq) employees.value = list
    } catch {
        if (my === employeesSeq) employeesError.value = true
    } finally {
        if (my === employeesSeq) employeesLoading.value = false
    }
}

const loadEmployeesLater = debounce(loadEmployees, 400)

// Смена магазина или интервала сбрасывает сотрудника и перезапрашивает свободных.
watch(() => [draft.fromDepartment?.id, draft.date, draft.start, draft.end], () => {
    draft.employeeId = ''
    formError.value = ''
    employeesSeq++
    employees.value = []
    loadEmployeesLater()
})

async function submit() {
    if (!canSubmit.value || !department.value) return
    if (instantOf(draft.date!, draft.start!) < wallNow()) {
        formError.value = 'Дата и время начала не могут быть раньше текущего времени'
        return
    }
    const employee = employees.value.find(item => item.id === draft.employeeId)
    const payload: Record<string, unknown> = {
        date: draft.date,
        dttm_work_start_at: buildTimestamp(draft.date!, draft.start!),
        dttm_work_end_at: buildTimestamp(draft.date!, draft.end!),
        department: department.value.id,
        position: draft.positionId,
        requested_department: draft.fromDepartment!.id,
        requested_user: draft.employeeId,
    }
    if (draft.comment.trim()) payload.comment = draft.comment.trim()
    saving.value = true
    try {
        await exchangeApi.createOne(payload)
        exchangeToast.success('Запрос отправлен', { text: `${fullName(employee)} · ${rowDateLabel(draft.date!)} · ${draft.start}–${draft.end}`, key: 'request-create' })
        draft.employeeId = ''
        draft.comment = ''
        draft.commentOpen = false
        emit('created')
    } catch (error) {
        exchangeToast.error('Запрос не отправлен', { text: errorText(error, 'Попробуйте ещё раз'), key: 'request-create' })
        if ((error as { kind?: string })?.kind === 'network') emit('created')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    void directory.searchDepartments('')
    // Черновик после смены вкладки: список свободных подгружается без сброса выбора.
    void loadEmployees()
})
</script>
